import { useTimeEventsOfTheDay } from "@kalyzee/kast-web-sdk";
import { MeetingOutputDTO, TimeEventTargetModels } from "@kalyzee/kast-web-sdk/http";
import { PrimaryButton, ReactIcons } from "@kalyzee/kalyzee-ui";
import { BannerButton } from "../../../../components/common/BannerButton";
import { audioContextIsReadyToUse, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MasterUI } from "../../../../components/common/MasterUI";
import { MediaSourcePicker, MediaGlobalControls } from "../../../../components/common/MediaSourcePicker";
import { PageMasterMode, usePageContext } from "../../../../components/navigation/PageContext";
import { AnimatedBackground } from "../../../../components/utils/AnimatedBackground";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { useSdkContext } from "../../../../contexts/sdk/context";
import { useMeetingSources } from "../../../../hooks/meeting";
import { ElapsedTimerPill } from "../../../../components/utils/ElapsedTimerPill";
import { getExpectedRoleInfo } from "../../../../helpers/meeting";
import { isExpert } from "../../../../helpers/roles";
import { getNormalUser } from "../../../../helpers/user";
import { getAudioContextForVolumeAnalyzer } from "../../../../helpers/audio";
import styles from "./lobby.module.css";

export const MeetingLobbyPage = () => {
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);
  const user = useAppStoreSelector(state => state.user);
  const sdkCtx = useSdkContext();

  const sources = useMeetingSources();

  const id = pageContext?.params?.id ?? "";
  const date = useMemo(() => new Date(), []);

  const { timeEvents } = useTimeEventsOfTheDay(
    sdkCtx.client,
    date,
    user ? { type: "user", id: user._id } : undefined,
    !id || !user,
    {
      meeting: id,
      targets: [TimeEventTargetModels.Meeting],
      populate: { 
        actor: { identity: true }, vodsCount: true },
    },
  );

  const meeting: MeetingOutputDTO | undefined = useMemo(() => {
    return timeEvents?.length ? (timeEvents[0].target as MeetingOutputDTO) : undefined;
  }, [timeEvents]);

  const vodsCount = meeting?.vodsCount ?? 0;

  const roleInfo = useMemo(() => {
    if (!meeting || !user) return { spectator: false, expert: false };
    return getExpectedRoleInfo(meeting, user._id, user.role);
  }, [meeting, user]);

  const onJoin = useCallback(async () => {
    // The click is the user gesture — ensure audio context is unlocked
    await audioContextIsReadyToUse(getAudioContextForVolumeAnalyzer());
    // Prefer the resolved meeting.id — URL param may be a shortId or provider-scoped id.
    const id = meeting?.id ?? pageContextRef.current?.params?.id;
    if (!id) return;
    pageContextRef.current?.goTo(PageMasterMode.MEETING_DASHBOARD, { id }, { queries: { provider: undefined } });
  }, [meeting]);

  const onGoToVod = useCallback(() => {
    const id = meeting?.id ?? pageContextRef.current?.params?.id;
    if (!id) return;
    pageContextRef.current?.goTo(
      PageMasterMode.LIST,
      undefined,
      { queries: { vodId: id } },
    );
  }, [meeting]);

  const meetingInfo = useMemo(() => {
    if (!meeting) return undefined;

    const sessions: any[] = (meeting as any).sessions ?? [];
    const expertNames = sessions
      .filter((s: any) => s.role && isExpert(s.role))
      .map((s: any) => {
        const identity = s.identity;
        return identity ? getNormalUser(identity as any).displayName : undefined;
      })
      .filter(Boolean);

    const participantCount = sessions.length;

    return {
      name: meeting.name,
      expertNames,
      participantCount,
      state: meeting.state,
      startedAt: meeting.startedAt,
    };
  }, [meeting]);

  return (
    <MasterUI>
      <AnimatedBackground contentClassName={styles.container}>
        <div className={styles.card}>
          {meetingInfo && (
            <div className={styles.infoSection}>
              <div className={styles.infoMain}>
                {meetingInfo.name && (
                  <h2 className={styles.meetingName}>{meetingInfo.name}</h2>
                )}
                {roleInfo?.spectator && (
                  <span className={`${styles.roleBadge} ${styles.roleSpectator}`}>Spectateur</span>
                )}
                {roleInfo?.expert && (
                  <span className={`${styles.roleBadge} ${styles.roleExpert}`}>Expert</span>
                )}
              </div>

              <div className={styles.infoDetails}>
                {meetingInfo.expertNames.length > 0 && (
                  <div className={styles.infoRow}>
                    <ReactIcons.Home style={{ width: 16, height: 16, flexShrink: 0 }} />
                    <span>
                      {meetingInfo.expertNames.length <= 3
                        ? meetingInfo.expertNames.join(", ")
                        : `${meetingInfo.expertNames.slice(0, 2).join(", ")} et ${meetingInfo.expertNames.length - 2} autres`}
                    </span>
                  </div>
                )}
                <div className={styles.infoRow}>
                  <ReactIcons.Eye style={{ width: 16, height: 16, flexShrink: 0 }} />
                  <span>{meetingInfo.participantCount} participant{meetingInfo.participantCount !== 1 ? "s" : ""}</span>
                </div>
                {meetingInfo.state === MeetingState.RUNNING && meetingInfo.startedAt && (
                  <ElapsedTimerPill since={new Date(meetingInfo.startedAt)} />
                )}
              </div>
            </div>
          )}

          <MediaSourcePicker
            sources={sources}
            className={styles.mediaSelector}
            // We can ignore gesture errors
            // here because this component is only used to configure the sources
            // it will be mounted again when joining the meeting
            // and there wont be gesture errors anymore
            ignoreGestureError
          />
          <MediaGlobalControls sources={sources} className={styles.globalControls} />

          <PrimaryButton className={styles.joinButton} onClick={onJoin}>
            Rejoindre le meeting
          </PrimaryButton>

          {vodsCount > 0 && (
            <BannerButton
              icon={<ReactIcons.PlayScreens />}
              title="Voir les replays"
              subtitle="Retrouvez vos cours enregistrés"
              background="linear-gradient(135deg, var(--color-element-calendar-item-join-vod-bg) 0%, var(--color-element-calendar-item-join-vod-bg) 100%)"
              onClick={onGoToVod}
            />
          )}
        </div>
      </AnimatedBackground>
    </MasterUI>
  );
};
